import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  fetchSacredProfile,
  saveSacredProfile,
  fetchAlertsToday,
  sendTestNotification,
} from "@/lib/sacred-alerts/sacred-alerts-api";
import { Loader2, Bell, Cake } from "lucide-react";

const DEITY_OPTIONS = ["shiva", "vishnu", "ganesha", "devi", "rama", "krishna"];

export function SacredProfileSettings({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    timezone: "Asia/Kolkata",
    birthdate_gregorian: "",
    preferred_deities: [],
    preferred_sadhanas: "",
    notification_prefs: {
      email: false,
      whatsapp_enabled: false,
      push: false,
      inapp: true,
      daily_reminder: false,
      daily_reminder_time: "06:00",
      festival_reminders: true,
      sadhana_streak_reminders: true,
    },
  });
  const [birthTithi, setBirthTithi] = useState(null);
  const [nextBirthday, setNextBirthday] = useState(null);
  const [message, setMessage] = useState("");

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await fetchSacredProfile(token);
      const sp = data.sacred_profile || {};
      setForm({
        name: sp.name || data.profile?.name || "",
        phone: sp.phone || data.profile?.phone || "",
        timezone: sp.timezone || "Asia/Kolkata",
        birthdate_gregorian: sp.birthdate_gregorian || "",
        preferred_deities: sp.preferred_deities || [],
        preferred_sadhanas: (sp.preferred_sadhanas || []).join(", "),
        notification_prefs: {
          email: false,
          whatsapp_enabled: false,
          push: false,
          inapp: true,
          daily_reminder: false,
          daily_reminder_time: "06:00",
          festival_reminders: true,
          sadhana_streak_reminders: true,
          ...(sp.notification_prefs || {}),
        },
      });
      setBirthTithi(sp.birth_tithi);
      setNextBirthday(sp.next_tithi_birthday);
      const today = await fetchAlertsToday(token);
      setAlerts(today.alerts || []);
      setBirthTithi(today.sacred_profile?.birth_tithi ?? sp.birth_tithi);
      setNextBirthday(today.sacred_profile?.next_tithi_birthday ?? sp.next_tithi_birthday);
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Could not load profile");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [token]);

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const data = await saveSacredProfile(token, {
        name: form.name,
        timezone: form.timezone,
        phone: form.phone,
        birthdate_gregorian: form.birthdate_gregorian || undefined,
        preferred_deities: form.preferred_deities,
        preferred_sadhanas: form.preferred_sadhanas
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        notification_prefs: form.notification_prefs,
      });
      setBirthTithi(data.sacred_profile?.birth_tithi);
      setNextBirthday(data.sacred_profile?.next_tithi_birthday);
      setMessage("Profile saved — birth tithi recomputed if date changed.");
      const today = await fetchAlertsToday(token);
      setAlerts(today.alerts || []);
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const testAlert = async () => {
    try {
      await sendTestNotification(token);
      setMessage("Test notification sent (check email if enabled).");
      load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Test failed");
    }
  };

  const toggleDeity = (id) => {
    setForm((f) => {
      const set = new Set(f.preferred_deities);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, preferred_deities: [...set] };
    });
  };

  if (!token) return null;
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;

  return (
    <div className="space-y-6" data-testid="sacred-profile-settings">
      <Card className="bg-white/90 shadow-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Cake className="w-5 h-5" /> Calendar & birth tithi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Display name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Timezone</Label>
              <Input
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                placeholder="Asia/Kolkata"
              />
            </div>
            <div>
              <Label>WhatsApp phone (with country code)</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+9689xxxxxxx"
              />
            </div>
            <div>
              <Label>Birth date (Gregorian)</Label>
              <Input
                type="date"
                value={form.birthdate_gregorian}
                onChange={(e) => setForm({ ...form, birthdate_gregorian: e.target.value })}
              />
            </div>
          </div>
          {birthTithi && (
            <p className="text-muted-foreground">
              Birth tithi: <strong>{birthTithi.full_name || birthTithi.name}</strong>
              {nextBirthday && <> · Next match: {nextBirthday}</>}
            </p>
          )}
          <div>
            <Label className="mb-2 block">Preferred deities</Label>
            <div className="flex flex-wrap gap-2">
              {DEITY_OPTIONS.map((d) => (
                <Badge
                  key={d}
                  variant={form.preferred_deities.includes(d) ? "default" : "outline"}
                  className="cursor-pointer capitalize"
                  onClick={() => toggleDeity(d)}
                >
                  {d}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <Label>Preferred sādhanas (comma-separated ids)</Label>
            <Input
              value={form.preferred_sadhanas}
              onChange={(e) => setForm({ ...form, preferred_sadhanas: e.target.value })}
              placeholder="shiva_panchakshari, gayatri"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/90 shadow-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Bell className="w-5 h-5" /> Notification channels
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {[
            ["inapp", "In-app alerts"],
            ["email", "Email (Resend)"],
            ["whatsapp_enabled", "WhatsApp reminders"],
            ["push", "Push (mobile-ready)"],
            ["daily_reminder", "Daily sādhana reminder"],
            ["daily_reminder_time", "Daily reminder time (UTC)"],
            ["festival_reminders", "Festival & vrata reminders"],
            ["sadhana_streak_reminders", "Streak encouragement"],
          ].map(([key, label]) =>
            key === "daily_reminder_time" ? (
              <div key={key} className="flex items-center gap-2">
                <span>{label}</span>
                <input
                  type="time"
                  value={form.notification_prefs.daily_reminder_time || "06:00"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      notification_prefs: { ...form.notification_prefs, daily_reminder_time: e.target.value },
                    })
                  }
                  className="h-8 rounded border border-input bg-background px-2 text-sm"
                />
              </div>
            ) : (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.notification_prefs[key]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      notification_prefs: { ...form.notification_prefs, [key]: e.target.checked },
                    })
                  }
                />
                {label}
              </label>
            )
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save profile"}
            </Button>
            <Button size="sm" variant="outline" onClick={testAlert}>
              Send test alert
            </Button>
          </div>
          {message && <p className="text-xs text-muted-foreground">{message}</p>}
        </CardContent>
      </Card>

      {alerts.length > 0 && (
        <Card className="bg-white/90 shadow-card">
          <CardHeader>
            <CardTitle className="text-sm">Today&apos;s alerts (preview)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {alerts.map((a, i) => (
              <div key={i} className="border-b pb-2 last:border-0">
                <Badge variant="outline" className="mb-1">{a.kind}</Badge>
                <p className="font-medium">{a.title}</p>
                <p className="text-muted-foreground">{a.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
